const SESSION_KEY = "break_agency_session_v1";
const TOKEN_KEY = "break_agency_jwt_token";
export const SESSION_CHANGED_EVENT = "break-agency-session-changed";

export const Roles = {
  SUPER_ADMIN: "super_admin",
  ADMIN: "admin",
  BRAND: "brand",
  CREATOR: "creator",
  TALENT_MANAGER: "talent-manager",
  BUYER: "buyer",
  SELLER: "seller",
  BUYER_SELLER: "buyer-seller",
  AGENT: "agent",
  VALUATION: "valuation"
};

const rolePriority = [
  Roles.SUPER_ADMIN,
  Roles.ADMIN,
  Roles.TALENT_MANAGER,
  Roles.BRAND,
  Roles.CREATOR,
  Roles.SELLER,
  Roles.BUYER_SELLER,
  Roles.BUYER,
  Roles.AGENT,
  Roles.VALUATION
];

function derivePrimaryRole(roles = []) {
  for (const role of rolePriority) {
    if (roles.includes(role)) return role;
  }
  return roles[0] || "visitor";
}

function emitSessionChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(SESSION_CHANGED_EVENT));
  }
}

export function getSession() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setSession(session) {
  if (typeof window === "undefined") return;
  if (!session) {
    localStorage.removeItem(SESSION_KEY);
    // Clean up legacy keys
    localStorage.removeItem("role");
    localStorage.removeItem("user");
  } else {
    const payload = { ...session, roles: Array.from(new Set(session.roles || [])) };
    localStorage.setItem(SESSION_KEY, JSON.stringify(payload));
  }
  emitSessionChanged();
}

export function clearSession() {
  setSession(null);
}

export function signInSession(data) {
  const session = {
    email: data.email,
    name: data.name || data.email,
    avatar: data.avatar || null,
    roles: data.roles ? Array.from(new Set(data.roles)) : [],
    provider: data.provider || "manual",
    signedInAt: Date.now()
  };
  setSession(session);
  return session;
}

// API-based auth
export function setAuthToken(token) {
  if (typeof window === "undefined") return;
  if (!token) {
    localStorage.removeItem(TOKEN_KEY);
  } else {
    localStorage.setItem(TOKEN_KEY, token);
  }
}

export function getAuthToken() {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export async function loginWithAPI(email, password) {
  const { loginUser } = await import("../api/client.js");
  const response = await loginUser({ email, password });
  
  console.log("[session] API response:", response);
  
  // Store JWT token
  setAuthToken(response.token);
  
  // Create session from user data
  const session = {
    email: response.user.email,
    name: response.user.name || response.user.email,
    avatar: response.user.avatarUrl || null,
    roles: [response.user.role.toLowerCase()],
    provider: "api",
    signedInAt: Date.now(),
    id: response.user.id
  };
  console.log("[session] Created session:", session);
  setSession(session);
  return session;
}

export async function loginWithGoogleAPI(token) {
  const { loginWithGoogleToken } = await import("../api/client.js");
  const response = await loginWithGoogleToken({ token });

  setAuthToken(response.token);

  const session = {
    email: response.user.email,
    name: response.user.name || response.user.email,
    avatar: response.user.avatarUrl || null,
    roles: [response.user.role.toLowerCase()],
    provider: "google",
    signedInAt: Date.now(),
    id: response.user.id
  };
  setSession(session);
  return session;
}

export async function registerWithAPI(email, name, role = "BUYER") {
  const { registerUser } = await import("../api/client.js");
  const response = await registerUser({ email, name, role });
  
  // Store JWT token
  setAuthToken(response.token);
  
  // Create session from user data
  const session = {
    email: response.user.email,
    name: response.user.name || response.user.email,
    avatar: response.user.avatarUrl || null,
    roles: [response.user.role.toLowerCase()],
    provider: "api",
    signedInAt: Date.now(),
    id: response.user.id
  };
  setSession(session);
  return session;
}

export async function refreshSessionFromAPI() {
  const token = getAuthToken();
  if (!token) return null;
  
  try {
    const { getCurrentUser } = await import("../api/client.js");
    const user = await getCurrentUser();
    
    const session = {
      email: user.email,
      name: user.name || user.email,
      avatar: user.avatarUrl || null,
      roles: [user.role.toLowerCase()],
      provider: "api",
      signedInAt: Date.now(),
      id: user.id
    };
    setSession(session);
    return session;
  } catch (error) {
    // Token expired or invalid
    clearSession();
    setAuthToken(null);
    return null;
  }
}

export function hasRole(role) {
  return !!getSession()?.roles?.includes(role);
}

export function hasAnyRole(roles) {
  const session = getSession();
  if (!session?.roles) return false;
  return roles.some((role) => session.roles.includes(role));
}

/**
 * Clean up legacy localStorage keys that are no longer used
 * Called on app initialization to prevent stale data accumulation
 */
export function cleanupLegacyStorage() {
  if (typeof window === "undefined") return;
  
  const legacyKeys = [
    "role", // Replaced by session roles array
    "user", // Replaced by break_agency_session_v1
    "buyer-active-search", // Now in API /api/saved-searches
    "home_saved_searches_v1", // Now in API /api/saved-searches
    "home_viewing_feedback_v1", // Now in API /api/viewings/:id/feedback
    "home_submitted_offers_v1", // Now in API /api/offers exclusively
    "home_seller_profiles_v1", // Now in API /api/profile/me
    "home_user_profiles_v1" // Now in API /api/profile/me
  ];
  
  legacyKeys.forEach(key => {
    try {
      if (localStorage.getItem(key)) {
        console.log(`[cleanup] Removing legacy localStorage key: ${key}`);
        localStorage.removeItem(key);
      }
    } catch (error) {
      console.warn(`[cleanup] Failed to remove key ${key}:`, error);
    }
  });
}
